import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75, color = "currentColor" }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    x: ['M18 6 6 18', 'M6 6l12 12'],
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    check: 'M20 6L9 17l-5-5',
    map: ['M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z', 'M8 2v16', 'M16 6v16'],
    clock: ['M12 2v20', 'M12 12l3 3', 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z'],
    calendar: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M21 8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z']
};

export default function TripSheetModal({ trip, instance, allocations = [], onClose, operatorId }) {
    // If instance is null, this is a one-time trip
    const data = instance || trip;
    const isRecurring = !!instance;
    const dateStr = data.tripDate 
        ? new Date(data.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

    // Fallback info
    const vehicle = data.vehicleId || trip.vehicleId;
    const driver = data.driverId || trip.driverId;

    // Route stops lookup map (stopId → name)
    const [routeStopsMap, setRouteStopsMap] = useState({});

    useEffect(() => {
        if (!trip.routeId) return;
        const routeId = trip.routeId?._id || trip.routeId;
        axios.get(`${API}/route-stops/${routeId}`)
            .then(res => {
                const stops = Array.isArray(res.data) ? res.data : (res.data.stops || []);
                const map = {};
                stops.forEach(s => { map[s._id] = s.name || s.stopName || s.address || 'Stop'; });
                setRouteStopsMap(map);
            })
            .catch(() => {});
    }, [trip.routeId]);

    // Use passed passengers or fallback to allocations for one-time trips
    let passengersList = data.passengers || [];
    if (!isRecurring) {
        const tripAllocations = allocations.filter(a => (a.tripId?._id || a.tripId) === trip._id);
        passengersList = tripAllocations.map(a => ({
            passengerId: a.passengerId,
            attendanceStatus: a.status || 'Pending'
        }));
    }

    const stopsList = data.stops || [];
    const startOdometer = (data.startOdometer != null && data.startOdometer !== '') ? data.startOdometer : (data.odometer != null && data.odometer !== '') ? data.odometer : '';
    const endOdometer = (data.endOdometer != null && data.endOdometer !== '') ? data.endOdometer : '';
    const totalDistance = (() => {
        const s = parseFloat(startOdometer);
        const e = parseFloat(endOdometer);
        if (!isNaN(s) && !isNaN(e)) return Math.max(0, e - s);
        return data.totalDistance ?? '';
    })();

    const getStopName = (stop) => {
        const rawId = stop.stopId?._id || stop.stopId;
        return stop.stopId?.name || stop.stopId?.stopName || routeStopsMap[rawId] || (rawId ? String(rawId).slice(-6).toUpperCase() : 'Stop');
    };

    const exportTripSheet = () => {
        const esc = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const rows = [
            ['Trip Sheet'],
            ['Trip Name', trip.tripName || trip.clientId?.companyName || 'Adhoc Trip'],
            ['Date', dateStr],
            ['Status', data.status || 'Scheduled'],
            ['Vehicle', vehicle?.plateNumber || vehicle?.registrationNumber || ''],
            ['Vehicle Make/Model', `${vehicle?.make || ''} ${vehicle?.model || ''}`.trim()],
            ['Driver', driver?.name || ''],
            ['Driver Phone', driver?.phoneNumber || ''],
            ['Schedule', `${trip.startTime || ''} to ${trip.endTime || ''}`],
            ['Frequency', isRecurring ? 'Recurring' : 'One-Time'],
            [],
            ['Pre-Trip Inspection'],
            ['Start Odometer', startOdometer],
            ['End Odometer', endOdometer],
            ['Total Distance', totalDistance !== '' ? `${totalDistance} km` : ''],
            ['Fuel Level Check', data.fuelCheck ? 'Passed' : 'Not Checked'],
            ['Tyre Condition Check', data.tyreCheck ? 'Passed' : 'Not Checked'],
            ['Emergency Kit Present', data.emergencyKit ? 'Passed' : 'Not Checked'],
            ['Vehicle Cleaning Check', data.cleaningCheck ? 'Passed' : 'Not Checked'],
            ['Submitted At', data.preTripSubmittedAt ? new Date(data.preTripSubmittedAt).toLocaleString('en-GB') : ''],
            [],
            ['Stop Tracking & Route'],
            ['Stop/Location', 'Status', 'Arrived At']
        ];

        if (trip.tripType === 'on_demand') {
            rows.push(['Pickup Location', trip.pickupLocation || '', '']);
            rows.push(['Drop Location', trip.dropLocation || '', '']);
        } else if (stopsList.length === 0) {
            rows.push(['No stops recorded', '', '']);
        } else {
            stopsList.forEach(stop => {
                rows.push([
                    getStopName(stop),
                    stop.arrived ? 'Arrived' : 'Pending',
                    stop.arrivedAt ? new Date(stop.arrivedAt).toLocaleString('en-GB') : ''
                ]);
            });
        }

        rows.push([]);
        rows.push(['Passenger Boarding Manifest']);
        rows.push(['Passenger Name', 'Employee ID', 'Status']);

        if (passengersList.length === 0) {
            rows.push(['No passengers allocated', '', '']);
        } else {
            passengersList.forEach(p => {
                const passenger = p.passengerId;
                rows.push([
                    passenger?.name || 'Unknown',
                    passenger?.employeeId || '',
                    p.attendanceStatus || 'Pending'
                ]);
            });
        }

        const csvContent = rows.map(row => row.map(esc).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const fileBase = (trip.tripName || 'trip-sheet').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
        const exportId = data._id || trip._id;
        a.href = url;
        a.download = `${fileBase || 'trip-sheet'}-${exportId ? String(exportId).slice(-8) : 'export'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="md-modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
            <div className="md-modal" style={{ maxWidth: 800, width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header" style={{ background: '#063A5C', color: 'white', borderBottom: 'none' }}>
                    <div>
                        <h3 className="md-modal-title" style={{ color: 'white' }}>Trip Sheet</h3>
                        <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>
                            {trip.tripName || trip.clientId?.companyName || 'Adhoc Trip'} • {dateStr}
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button
                            type="button"
                            onClick={exportTripSheet}
                            title="Export Trip Sheet"
                            style={{
                                height: 32,
                                padding: '0 12px',
                                borderRadius: 8,
                                border: '1px solid rgba(255,255,255,0.28)',
                                background: 'rgba(255,255,255,0.14)',
                                color: 'white',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: 'pointer'
                            }}
                        >
                            <Icon d={icons.download} size={15} />
                            Export
                        </button>
                        <button className="md-modal-close" style={{ color: 'white' }} onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                </div>
                <div className="md-modal-body" style={{ padding: '24px', overflowY: 'auto', background: '#f8fafc' }}>
                    
                    {/* Header Summary */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                        <div style={{ background: 'white', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Status</div>
                            <span className={`md-badge md-badge-${(data.status || 'Scheduled').toLowerCase()}`} style={{ fontSize: 12 }}>{data.status || 'Scheduled'}</span>
                        </div>
                        <div style={{ background: 'white', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Vehicle</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{vehicle?.plateNumber || vehicle?.registrationNumber || '—'}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{vehicle?.make || ''} {vehicle?.model || ''}</div>
                        </div>
                        <div style={{ background: 'white', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Driver</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{driver?.name || '—'}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{driver?.phoneNumber || ''}</div>
                        </div>
                        <div style={{ background: 'white', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Schedule</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{trip.startTime || '—'} to {trip.endTime || '—'}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{isRecurring ? 'Recurring' : 'One-Time'}</div>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                        {/* Pre-Trip Inspection */}
                        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9', fontWeight: 800, color: '#334155', fontSize: 12, textTransform: 'uppercase' }}>
                                Pre-Trip Inspection
                            </div>
                            <div style={{ padding: 16 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                                    <div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>Start Odometer</div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                                            {(data.startOdometer != null && data.startOdometer !== '') ? data.startOdometer : (data.odometer != null && data.odometer !== '') ? data.odometer : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>End Odometer</div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                                            {(data.endOdometer != null && data.endOdometer !== '') ? data.endOdometer : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>Total Distance</div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                                            {(() => {
                                                const startVal = (data.startOdometer != null && data.startOdometer !== '') ? data.startOdometer : (data.odometer != null && data.odometer !== '') ? data.odometer : null;
                                                const endVal = (data.endOdometer != null && data.endOdometer !== '') ? data.endOdometer : null;
                                                if (startVal != null && endVal != null) {
                                                    const s = parseFloat(startVal);
                                                    const e = parseFloat(endVal);
                                                    if (!isNaN(s) && !isNaN(e)) {
                                                        return `${Math.max(0, e - s)} km`;
                                                    }
                                                }
                                                return '—';
                                            })()}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {[
                                        { label: 'Fuel Level Check', val: data.fuelCheck },
                                        { label: 'Tyre Condition Check', val: data.tyreCheck },
                                        { label: 'Emergency Kit Present', val: data.emergencyKit },
                                        { label: 'Vehicle Cleaning Check', val: data.cleaningCheck }
                                    ].map(item => (
                                        <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{item.label}</span>
                                            {item.val ? (
                                                <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700 }}>
                                                    <Icon d={icons.check} size={14} color="#10b981" /> Passed
                                                </span>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Not Checked</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {data.preTripSubmittedAt && (
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 12, textAlign: 'right' }}>
                                        Submitted at: {new Date(data.preTripSubmittedAt).toLocaleString('en-GB')}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Stops & Location tracking */}
                        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9', fontWeight: 800, color: '#334155', fontSize: 12, textTransform: 'uppercase' }}>
                                Stop Tracking & Route
                            </div>
                            <div style={{ padding: 16 }}>
                                {trip.tripType === 'on_demand' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        <div style={{ display: 'flex', gap: 12 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', marginTop: 4 }} />
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Pickup Location</div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{trip.pickupLocation}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 12 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', marginTop: 4 }} />
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Drop Location</div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{trip.dropLocation}</div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {stopsList.length === 0 ? (
                                            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No stops recorded.</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                {stopsList.map((stop, idx) => {
                                                    const stopName = getStopName(stop);
                                                    return (
                                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f8fafc', borderRadius: 8, borderLeft: `3px solid ${stop.arrived ? '#10b981' : '#cbd5e1'}` }}>
                                                        <div>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{stopName}</div>
                                                            {stop.arrived && stop.arrivedAt && (
                                                                <div style={{ fontSize: 10, color: '#64748b' }}>Arrived at {new Date(stop.arrivedAt).toLocaleTimeString('en-GB')}</div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            {stop.arrived ? (
                                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: 12 }}>Arrived</span>
                                                            ) : (
                                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 12 }}>Pending</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Passenger Manifest */}
                    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9', fontWeight: 800, color: '#334155', fontSize: 12, textTransform: 'uppercase' }}>
                            Passenger Boarding Manifest
                        </div>
                        <div style={{ padding: 16 }}>
                            {passengersList.length === 0 ? (
                                <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No passengers allocated to this trip.</div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
                                    {passengersList.map((p, idx) => {
                                        const passenger = p.passengerId;
                                        const statusColor = p.attendanceStatus === 'Boarded' ? '#16a34a' : p.attendanceStatus === 'Present' ? '#10b981' : p.attendanceStatus === 'No Show' ? '#f59e0b' : p.attendanceStatus === 'Absent' ? '#ef4444' : '#64748b';
                                        const statusBg = p.attendanceStatus === 'Boarded' ? '#dcfce7' : p.attendanceStatus === 'Present' ? '#d1fae5' : p.attendanceStatus === 'No Show' ? '#fef3c7' : p.attendanceStatus === 'Absent' ? '#fee2e2' : '#f1f5f9';
                                        
                                        return (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: passenger?.gender === 'Female' ? '#fdf2f8' : '#eff6ff', color: passenger?.gender === 'Female' ? '#db2777' : '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Icon d={icons.user} size={18} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{passenger?.name || 'Unknown'}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{passenger?.employeeId || '—'}</div>
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: 10, fontWeight: 800, color: statusColor, background: statusBg, padding: '4px 8px', borderRadius: 12 }}>
                                                        {p.attendanceStatus || 'Pending'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
